import { useState, useEffect } from 'react'
import { Button } from '../../components/ui/Button.tsx'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card.tsx'
import LoadingSpinner from '../../components/ui/LoadingSpinner.tsx'
import { MapPin, Clock, PoundSterling, Sparkles } from 'lucide-react'
import { apiGet, apiPostForm } from '../../lib/api.ts'

interface Job {
  id: number
  title: string
  description: string
  location_city: string
  location_county: string
  hourly_rate: number | null
  day_rate: number | null
  start_date: string
  end_date: string
  practice_name: string
  job_role_name: string
  application_status: string
  match_score: number
  qualification_match: boolean
  location_match: boolean
  availability_match: boolean
}

export default function JobsPage() {
  const [jobs, setJobs] = useState<Job[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [applyingTo, setApplyingTo] = useState<number | null>(null)
  const [notice, setNotice] = useState('')

  useEffect(() => {
    void fetchJobs()
  }, [])

  const fetchJobs = async () => {
    try {
      const data = await apiGet<{ jobs: Job[] }>('/locum/jobs.php?format=json')
      setJobs(data.jobs)
      setError('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load jobs.')
    } finally {
      setLoading(false)
    }
  }

  const handleApply = async (jobId: number) => {
    setApplyingTo(jobId)
    setNotice('')
    try {
      const data = await apiPostForm<{ success: boolean; message: string }>(
        '/locum/jobs.php?format=json',
        { job_id: jobId.toString(), action: 'apply' }
      )
      setNotice(data.message)
      await fetchJobs()
    } catch (err) {
      setNotice(err instanceof Error ? err.message : 'Unable to submit your application.')
    } finally {
      setApplyingTo(null)
    }
  }

  if (loading) {
    return <LoadingSpinner />
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="text-center text-red-600 bg-red-50 p-4 rounded-lg">
          {error}
        </div>
        <Button onClick={fetchJobs} className="w-full mt-4">
          Try Again
        </Button>
      </div>
    )
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-text-900">Matched Jobs</h1>
        <Button
          onClick={() => void fetchJobs()}
          variant="outline"
          size="sm"
        >
          Refresh
        </Button>
      </div>

      {notice && (
        <div className={`rounded-lg p-3 text-sm ${notice.toLowerCase().includes('success') || notice.toLowerCase().includes('submitted') ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-800'}`}>
          {notice}
        </div>
      )}

      {jobs.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-text-700">No published matches are available right now.</p>
          <p className="text-sm text-text-500 mt-2">Keep your profile, skills, and availability up to date to improve matching.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {jobs.map((job) => (
            <Card key={job.id} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle className="text-lg text-brand-700">{job.title}</CardTitle>
                    <CardDescription className="mt-1">{job.practice_name} · {job.job_role_name}</CardDescription>
                    <CardDescription className="mt-1 flex items-center gap-1">
                      <MapPin size={16} />
                      {[job.location_city, job.location_county].filter(Boolean).join(', ')}
                    </CardDescription>
                  </div>
                  <div className="rounded-full bg-brand-100 px-3 py-1 text-sm font-semibold text-brand-700">
                    {job.match_score}/100
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-green-600 font-semibold">
                      <PoundSterling size={16} />
                      {job.hourly_rate ? `£${job.hourly_rate}/hr` : job.day_rate ? `£${job.day_rate}/day` : 'Rate on request'}
                    </div>
                    <div className="flex items-center gap-1 text-text-600">
                      <Clock size={16} />
                      {new Date(job.start_date).toLocaleDateString()}
                      {job.end_date && job.end_date !== job.start_date ? ` - ${new Date(job.end_date).toLocaleDateString()}` : ''}
                    </div>
                  </div>

                  <p className="text-sm text-text-700">
                    {job.description}
                  </p>

                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className={`rounded-lg px-2 py-2 ${job.qualification_match ? 'bg-green-50 text-green-700' : 'bg-surface-100 text-text-600'}`}>Skills</div>
                    <div className={`rounded-lg px-2 py-2 ${job.location_match ? 'bg-green-50 text-green-700' : 'bg-surface-100 text-text-600'}`}>Location</div>
                    <div className={`rounded-lg px-2 py-2 ${job.availability_match ? 'bg-green-50 text-green-700' : 'bg-surface-100 text-text-600'}`}>Availability</div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1 rounded-full bg-surface-100 px-3 py-1 text-sm text-text-600">
                      <Sparkles size={14} />
                      {job.application_status || 'Ready to apply'}
                    </span>
                    {job.application_status ? (
                      <span className="text-sm font-medium text-text-500">Already in progress</span>
                    ) : (
                      <Button
                        onClick={() => void handleApply(job.id)}
                        disabled={applyingTo === job.id}
                        size="sm"
                        className="bg-brand-600 hover:bg-brand-700"
                      >
                        {applyingTo === job.id ? 'Applying...' : 'Apply Now'}
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
